import { useEffect, useState } from "react";
import { getCatalogLesson, lessons, type Lesson } from "../../shared/catalog";
import { docFromLesson, docToText, timelineShape } from "../../shared/lesson-model";
import { api, ApiError } from "../lib/api";
import { Link, useLocation } from "../lib/router";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import { LessonDocView } from "../components/LessonDocView";
import { StageStrip } from "../components/StageStrip";
import { Icon } from "../components/Icon";
import { Paywall } from "../components/Paywall";
import { SignInPanel } from "../components/SignInPanel";
import { pageTitle } from "../brand";

export default function LessonPage({ id }: { id: string }) {
  const cat = getCatalogLesson(id);
  const { search } = useLocation();
  const { me, favorites, toggleFavorite } = useSession();
  const toast = useToast();
  const [minutes, setMinutes] = useState(() => Number(search.get("minutes")) || cat?.duration || 40);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [locked, setLocked] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [needSignIn, setNeedSignIn] = useState<"" | "save">("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (cat) document.title = pageTitle(`${cat.id} ${cat.activity}`);
  }, [cat]);
  useEffect(() => {
    if (!cat) return;
    setLesson(null);
    setLocked(false);
    setLoadError("");
    api.lesson(cat.id).then((r) => setLesson(r.lesson)).catch((e: ApiError) => {
      if (e.code === "NO_ACCESS" || e.code === "SIGN_IN_REQUIRED") setLocked(true);
      else setLoadError(e.message);
    });
  }, [cat, me?.access.state]);
  useEffect(() => {
    if (cat) history.replaceState(null, "", `/lesson/${cat.id}${minutes === cat.duration ? "" : `?minutes=${minutes}`}`);
  }, [minutes, cat]);

  if (!cat) {
    return (
      <div className="page narrow empty">
        <h1>No lesson called “{id}”</h1>
        <p>Lesson codes run A1–A10, B1–B10, C1–C8, D1–D8, E1–E8 and F1–F6. <Link to="/">Browse all lessons</Link>.</p>
      </div>
    );
  }
  const idx = lessons.indexOf(cat);
  const prev = lessons[idx - 1];
  const next = lessons[idx + 1];
  const saved = favorites.has(cat.id);
  const doc = lesson ? docFromLesson(lesson, minutes) : null;

  const email = async () => {
    setSending(true);
    try {
      const r = await api.emailLesson({ lessonId: cat.id, duration: minutes === cat.duration ? undefined : minutes });
      toast(`Sent to ${r.to}`);
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page lesson-layout">
      <div className="lesson-body">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/">Lessons</Link> <span aria-hidden="true">/</span> <Link to={`/?section=${cat.section}`}>{cat.sectionName}</Link>
        </nav>
        {doc ? (
          <LessonDocView doc={doc} />
        ) : (
          <article className="doc">
            <header className="doc-head">
              <p className="doc-kicker"><span className="code">{cat.id}</span> {cat.topic}</p>
              <h1 className="doc-title">{cat.activity}</h1>
              <p className="doc-meta"><span>{cat.level}</span><span>{minutes} minutes</span><span>{cat.skill}</span></p>
            </header>
            <StageStrip stages={timelineShape(minutes)} size="lg" showLabels />
            <section className="doc-block doc-aims"><p><strong>Objective.</strong> {cat.objective}</p></section>
            {locked ? <Paywall /> : loadError ? <p className="form-error">{loadError}</p> : <p className="muted">Loading the lesson…</p>}
          </article>
        )}
        <nav className="prevnext" aria-label="Neighbouring lessons">
          {prev ? <Link to={`/lesson/${prev.id}`}><Icon name="back" /> {prev.id} {prev.activity}</Link> : <span />}
          {next && <Link to={`/lesson/${next.id}`}>{next.id} {next.activity} <Icon name="next" /></Link>}
        </nav>
      </div>

      <aside className="rail" aria-label="Lesson tools">
        <div className="rail-block">
          <label className="dial-label" htmlFor="retime">Re-time instantly <output>{minutes} min</output></label>
          <input id="retime" type="range" min={10} max={120} step={5} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} />
          <p className="muted small">
            {minutes === cat.duration ? `As written: ${cat.durationLabel}.` : minutes >= 55 ? "Longer period: the Extension becomes a timed stage." : "Stages rescaled from the handbook's own timings."}
            {minutes !== cat.duration && <> <button className="btn-link" onClick={() => setMinutes(cat.duration)}>Reset</button></>}
          </p>
        </div>
        <Link className="btn btn-primary btn-block" to={`/studio/${cat.id}${minutes !== cat.duration ? `?duration=${minutes}` : ""}`}>
          <Icon name="spark" /> Adapt for my class
        </Link>
        <p className="muted small rail-hint">Change the level, class size, setting, technology and context. The AI rewrites the lesson and explains every change.</p>
        {lesson && (
          <>
            <Link className="btn btn-block" to={`/teach/lesson/${cat.id}?minutes=${minutes}`}><Icon name="play" /> Teach mode</Link>
            <div className="rail-row">
              <button className={`btn btn-quiet${saved ? " is-on" : ""}`} aria-pressed={saved} onClick={() => (me?.user ? void toggleFavorite(cat.id) : setNeedSignIn("save"))}>
                <Icon name="heart" /> {saved ? "Saved" : "Save"}
              </button>
              {me?.access.active && <button className="btn btn-quiet" onClick={() => void email()} disabled={sending}><Icon name="mail" /> {sending ? "Sending…" : "Email"}</button>}
              <button className="btn btn-quiet" onClick={() => window.print()}><Icon name="print" /> Print</button>
              <button className="btn btn-quiet" onClick={() => void navigator.clipboard.writeText(docToText(doc!)).then(() => toast("Lesson copied as plain text"))}><Icon name="copy" /> Copy</button>
            </div>
          </>
        )}
        {needSignIn && <SignInPanel reason="Sign in to save lessons to your shelf." onDone={() => setNeedSignIn("")} />}
      </aside>
      <div className="mobile-cta">
        <Link className="btn btn-primary" to={`/studio/${cat.id}${minutes !== cat.duration ? `?duration=${minutes}` : ""}`}><Icon name="spark" /> Adapt</Link>
        {lesson && <Link className="btn" to={`/teach/lesson/${cat.id}?minutes=${minutes}`}><Icon name="play" /> Teach</Link>}
      </div>
    </div>
  );
}
